import React, { Children, useState, useEffect, useCallback } from 'react'
import cx from 'classnames'
import styles from './RadioGroupCards.module.scss'
import Radio from '../../atoms/Radio/Radio'
import { Tooltip } from 'views/components'
import Info from '../../atoms/Svgs/Info'
import { useHover } from 'react-use'

export interface IRadioCardFeature {
  label: string
  // Defaults to true. When false the feature is rendered as a muted "—" (not included).
  included?: boolean
}

interface IRadioGroupCards extends React.InputHTMLAttributes<HTMLInputElement> {
  children: React.ReactNode
  className?: string
  defaultSelected?: string | number
  label?: string
  labelHint?: string
  labelDescription?: string
  error?: string
}

const RadioGroupCards = ({
  children,
  className,
  defaultSelected,
  label,
  labelHint,
  labelDescription = '',
  onChange = () => {},
  error,
  ...props
}: IRadioGroupCards) => {
  const { name } = props
  const [selected, setSelected] = useState(defaultSelected)

  // Keep the selection in sync when the controlling value changes externally (e.g. form reset).
  useEffect(() => {
    setSelected(defaultSelected)
  }, [defaultSelected])

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.id !== selected) {
        setSelected(e.target.id)
        if (onChange) onChange(e)
      }
    },
    [onChange, selected],
  )

  const hasError = error && error.length > 0

  const classes = cx([styles.root, className], {
    [styles['has-error']]: hasError,
  })

  const [hoverableInfoIcon, isHovering] = useHover(
    <div className={styles['info-icon']}>
      <Info width={20} height={20} />
    </div>,
  )

  return (
    <div className={classes}>
      {label && (
        <label>
          {label}
          {hasError && ` - ${error as string}`}
          {labelHint && (
            <Tooltip isOpen={isHovering} body={labelHint}>
              {hoverableInfoIcon}
            </Tooltip>
          )}
        </label>
      )}
      <div className={styles.radios}>
        {Children.map(children, (radioButton: any) => {
          const { value, features, disabled: cardDisabled, ...other } = radioButton.props
          const isSelected = value === selected
          const isDisabled = props.disabled || cardDisabled
          const cardFeatures: IRadioCardFeature[] = features ?? []
          return (
            <div
              key={value}
              className={cx(styles.card, {
                [styles.active]: isSelected,
                [styles.disabled]: isDisabled,
              })}
            >
              <Radio
                {...other}
                name={name}
                onChange={handleChange}
                id={value}
                value={value}
                checked={isSelected}
                disabled={isDisabled}
              />
              {cardFeatures.length > 0 && (
                <ul className={styles.features}>
                  {cardFeatures.map((feature, index) => {
                    const isIncluded = feature.included !== false
                    return (
                      <li key={index} className={cx(styles.feature, { [styles['feature-excluded']]: !isIncluded })}>
                        <span className={styles['feature-icon']} aria-hidden="true">
                          {isIncluded ? (
                            <svg width={14} height={14} viewBox="0 0 14 14" fill="none">
                              <path
                                d="M2.5 7.5L5.5 10.5L11.5 4"
                                stroke="currentColor"
                                strokeWidth={1.75}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          ) : (
                            <svg width={14} height={14} viewBox="0 0 14 14" fill="none">
                              <path d="M3 7H11" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" />
                            </svg>
                          )}
                        </span>
                        <span className={styles['feature-label']}>{feature.label}</span>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default RadioGroupCards
